import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { urlFor } from "../../../sanity/lib/image";
import styles from "./styles.module.scss";
import Button from "../../Button";
import VimeoEmbed from "../../vimeo-embed";
import { portableTextComponents } from "../../../lib/portableTextComponents";
import dynamic from "next/dynamic";
import { Features } from "./Features";
import { IconRenderer } from "@/components/IconRenderer";

const MapBlock = dynamic(() => import("../../map"), {
  ssr: false,
  loading: () => {
    return (
      <div
        className="bg-neutral-250 w-100 flex align-center justify-content-center"
        style={{ height: '100%' }}
      >
        Loading Map...
      </div>
    )
  },
});

export function SplitImage({ title, titleColor, text, textColor, media, orientation, textAlignment, borderColor, buttons, backgroundType, backgroundColor, backgroundGradient, backgroundPosition, imageWidth, features, loading = false }) {

  const cleanedOrientation = orientation || "imageLeft";
  const cleanedTitleColor = titleColor || null;
  const cleanedTextColor = textColor || null;
  const cleanedBackgroundColor = backgroundColor || null;
  const cleanedAlignment = textAlignment || "left";
  const mediaItem = media?.[0];

  if (loading) {
    return (
      <section className={`${styles.splitImage} ${styles.skeleton}`}>
        <div className={styles.imageWrapper}>
          <div className={styles.skeletonBox}></div>
        </div>
        <div className={styles.textWrapper}>
          <div className={styles.skeletonTitle}></div>
          <div className={styles.skeletonText}></div>
          <div className={styles.skeletonText}></div>
          <div className={styles.skeletonButtons}></div>
        </div>
      </section>
    );
  }

  const renderMedia = () => {
    if (!mediaItem) return null;

    if (mediaItem._type === "icon" && mediaItem.icon) {
      return (
        <div className={styles.iconWrapper}>
          <IconRenderer icon={mediaItem.icon} className={styles.icon} />
        </div>
      );
    }

    if (mediaItem._type === "image") {
      const imageFit = mediaItem.objectFit || "cover";

      const imageUrl = mediaItem.url
        ? mediaItem.url // static string URL (like from /public)
        : mediaItem.asset
          ? urlFor(mediaItem).width(800).height(600).url()
          : null;

      return imageUrl ? (
        <Image
          className={styles.image}
          src={imageUrl}
          width={800}
          height={600}
          alt=""
          style={{ objectFit: imageFit }}
        />
      ) : null;
    }
    if (mediaItem._type === "video" && mediaItem.url) {
      return <VimeoEmbed embedUrl={mediaItem.url} />;
    }

    if (mediaItem._type === "map") {
      return <MapBlock
        markers={mediaItem.markers}
        center={[mediaItem.location.lat, mediaItem.location.lng]}
        height="100%"
      />
    }

    return null;
  };

  let styleObject = {};
  const backgroundStyleObject = {};

  if (borderColor !== null && borderColor !== undefined) styleObject.borderColor = `rgba(${borderColor.rgb.r},${borderColor.rgb.g},${borderColor.rgb.b},${borderColor.rgb.a})`

  if (backgroundType !== null) {
    if (backgroundType === "solid" && cleanedBackgroundColor !== null) backgroundStyleObject.backgroundColor = `rgba(${backgroundColor.rgb.r},${backgroundColor.rgb.g},${backgroundColor.rgb.b},${backgroundColor.rgb.a})`
    if (backgroundType === "gradient" && backgroundGradient?.length > 0) {
      const gradientColors = backgroundGradient.map((color) => `rgba(${color.color.rgb.r},${color.color.rgb.g},${color.color.rgb.b},${color.color.rgb.a})`);
      backgroundStyleObject.background = `linear-gradient(90deg, ${gradientColors.join(", ")})`;
    }
  }

  if (backgroundPosition === "contained") {
    styleObject = { ...styleObject, ...backgroundStyleObject };
  }

  return (
    <section
      id={title ? title.toLowerCase().replace(/\s+/g, '-') : undefined}
      className={`${styles.splitImage} ${styles[cleanedOrientation]} ${styles[`background--${backgroundPosition}`]}`}
      data-orientation={cleanedOrientation}
      style={styleObject}
    >
      {backgroundPosition === "fullWidth" && <div className={styles.backgroundPlaceholder} style={backgroundStyleObject} />}
      {cleanedOrientation === "imageTop" ? (
        <>
          {mediaItem && <div className={styles.imageWrapper} style={{ "--flex-basis": `${imageWidth}%` }}>{renderMedia()}</div>}
          <div className={`${styles.textWrapper} ${styles[cleanedAlignment]}`} style={{ "--flex-basis": `${100 - imageWidth}%` }}>
            {title && <h2
              className={styles.title}
              style={{ color: cleanedTitleColor !== null ? `rgba(${titleColor.rgb.r},${titleColor.rgb.g},${titleColor.rgb.b},${titleColor.rgb.a})` : {} }}
            >{title}</h2>}
            {text && (
              <div className={styles.text} style={{
                color: cleanedTextColor
                  ? `rgba(${cleanedTextColor.rgb.r},${cleanedTextColor.rgb.g},${cleanedTextColor.rgb.b},${cleanedTextColor.rgb.a})`
                  : undefined,
              }}>
                <PortableText
                  components={portableTextComponents}
                  value={text}
                />
              </div>
            )}
            {buttons?.length > 0 && (
              <div className={`${styles.buttonGroup} ${styles[cleanedAlignment]}`}>
                {buttons.map((button) =>
                  button?.isExternal ? (
                    <Button
                      type="link"
                      key={button.url}
                      href={button.url}
                      className={button.color || "primary"}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {button.text}
                    </Button>
                  ) : (
                    <Button
                      type="link"
                      key={button.url}
                      href={button.url}
                      className={button.color || "primary"}
                    >
                      {button.text}
                    </Button>
                  )
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {mediaItem && <div className={styles.imageWrapper} style={{ "--flex-basis": `${imageWidth}%` }}>{renderMedia()}</div>}
          <div className={`${styles.textWrapper} ${styles[cleanedAlignment]}`} style={{ "--flex-basis": `${100 - imageWidth}%` }}>
            {title && <h2
              className={styles.title}
              style={{ color: cleanedTitleColor !== null ? `rgba(${titleColor.rgb.r},${titleColor.rgb.g},${titleColor.rgb.b},${titleColor.rgb.a})` : {} }}
            >{title}</h2>}
            {text && (
              <div className={styles.text} style={{
                color: cleanedTextColor
                  ? `rgba(${cleanedTextColor.rgb.r},${cleanedTextColor.rgb.g},${cleanedTextColor.rgb.b},${cleanedTextColor.rgb.a})`
                  : undefined,
              }}>
                <PortableText components={portableTextComponents} value={text} />
              </div>
            )}
            {features && <Features features={features} />}
            {buttons?.length > 0 && (
              <div className={`${styles.buttonGroup} ${styles[cleanedAlignment]}`}>
                {buttons.map((button) =>
                  button?.isExternal ? (
                    <Button
                      type="link"
                      key={button.url}
                      href={button.url}
                      className={button.color || "primary"}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {button.text}
                    </Button>
                  ) : (
                    <Button
                      key={button.text}
                      type={button.onClick ? 'button' : 'link'}
                      href={button.onClick ? undefined : button.url}
                      className={button.color || "primary"}
                      onClick={button.onClick || undefined}
                      target={button.isExternal ? "_blank" : undefined}
                      rel={button.isExternal ? "noopener noreferrer" : undefined}
                    >
                      {button.text}
                    </Button>
                  )
                )}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );

}
